import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../auth/useAuth";
import { ADMIN_PERMISSIONS } from "../../auth/adminPermissions";
import RequirePermission from "../../auth/RequirePermission";
import AdminPageLayout from "../../components/midpoint/admin/layout/AdminPageLayout";
import AdminPageHeader from "../../components/midpoint/admin/layout/AdminPageHeader";
import AdminStatePanel from "../../components/midpoint/admin/layout/AdminStatePanel";
import {
  ALERT_SEVERITY_LABELS,
  DATE_RANGE_OPTIONS,
  DEFAULT_DATE_RANGE,
  LAUNCH_STATUS_LABELS,
  alertSeverityClass,
  formatCop,
  formatDateTime,
  formatPercent,
  launchStatusClass,
  trackAdminDashboardEvent,
} from "../../constants/automapAdminDashboardConstants";
import { formatDealerStatus } from "../../constants/automapDealerOnboardingConstants";
import { STATUS_LABELS } from "../../constants/automapDealerLeadConstants";
import { fetchAdminDashboard } from "../../services/automapAdminDashboardApi";
import { AdminAutomotiveNav } from "./automotive/AdminAutomotiveNav";
import { statusBadgeClass } from "./dealers/dealerAdminShared";
import "../../styles/adminDashboard.css";

function MetricCard({ label, value, href }) {
  const content = (
    <>
      <span className="admin-dashboard__metric-label">{label}</span>
      <strong className="admin-dashboard__metric-value">{value ?? "—"}</strong>
    </>
  );
  if (href) {
    return (
      <Link to={href} className="admin-dashboard__metric-card admin-dashboard__metric-card--link">
        {content}
      </Link>
    );
  }
  return <div className="admin-dashboard__metric-card">{content}</div>;
}

function BarRow({ label, value, max }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div className="admin-dashboard__bar-row">
      <span>{label}</span>
      <div className="admin-dashboard__bar-track">
        <div className="admin-dashboard__bar-fill" style={{ width: `${pct}%` }} />
      </div>
      <strong>{value}</strong>
    </div>
  );
}

function AdminDashboardPageContent() {
  const { hasPermission, isSuperAdmin } = useAuth();
  const canView =
    isSuperAdmin || hasPermission(ADMIN_PERMISSIONS.REAL_ESTATE_SOURCES_VIEW);

  const [range, setRange] = useState(DEFAULT_DATE_RANGE);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await fetchAdminDashboard(range);
      setDashboard(data.dashboard);
    } catch (err) {
      setError(err.message || "No se pudo cargar el dashboard admin.");
      setDashboard(null);
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => {
    if (!canView) return;
    trackAdminDashboardEvent("admin_dashboard_viewed", { range });
  }, [canView, range]);

  useEffect(() => {
    load();
  }, [load]);

  const handleRangeChange = (next) => {
    setRange(next);
    trackAdminDashboardEvent("admin_dashboard_date_range_changed", { range: next });
  };

  const handleAlertClick = (alert) => {
    trackAdminDashboardEvent("admin_alert_clicked", { alertType: alert.type, count: alert.count });
  };

  if (!canView) {
    return (
      <AdminPageLayout>
        <AdminStatePanel variant="empty" message="Se requiere rol admin/interno. TODO: reforzar control de roles por módulo AutoMapa." />
      </AdminPageLayout>
    );
  }

  if (loading) {
    return (
      <AdminPageLayout>
        <AdminPageHeader eyebrow="AutoMapa · Admin" title="Dashboard operativo" />
        <AdminAutomotiveNav active="dashboard" />
        <AdminStatePanel variant="loading" message="Agregando métricas del marketplace…" />
      </AdminPageLayout>
    );
  }

  if (error) {
    return (
      <AdminPageLayout>
        <AdminPageHeader eyebrow="AutoMapa · Admin" title="Dashboard operativo" />
        <AdminAutomotiveNav active="dashboard" />
        <AdminStatePanel variant="error" message={error} onRetry={load} retryLabel="Reintentar" />
      </AdminPageLayout>
    );
  }

  if (!dashboard) return null;

  const {
    summary,
    launchReadiness,
    alerts,
    dealerOps,
    inventoryOps,
    leadOps,
    importHealth,
    trialPerformance,
    marketplaceHealth,
    marketplaceAnalyticsSummary,
    foundingDealerSummary,
    planMonetizationSummary,
        cityExpansionSummary,
    partnershipSummary,
    buyerLaunchOverlay,
    recentActivity,
    isEmpty,
  } = dashboard;

  return (
    <AdminPageLayout className="admin-dashboard-layout">
      <div className="admin-dashboard">
        <AdminPageHeader
          eyebrow="AutoMapa · Admin"
          title="Dashboard operativo"
          description="Centro de comando para salud del marketplace, concesionarios, inventario y leads."
        />
        <AdminAutomotiveNav active="dashboard" />

        {process.env.NODE_ENV === "development" ? (
          <p className="admin-dashboard__dev-note">
            Datos agregados desde repositorios locales JSON. Algunas métricas usan placeholders hasta
            M17/M18.
          </p>
        ) : null}

        <div className="admin-dashboard__header-row">
          <div className="admin-dashboard__range">
            <label htmlFor="admin-dashboard-range">Periodo</label>
            <select
              id="admin-dashboard-range"
              value={range}
              onChange={(e) => handleRangeChange(e.target.value)}
            >
              {DATE_RANGE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
            <Link className="admin-btn admin-btn--secondary" to="/admin/dealers/new">
              Nuevo concesionario
            </Link>
            <Link className="admin-btn admin-btn--secondary" to="/admin/inventory/imports/new">
              Nueva importación
            </Link>
          </div>
        </div>

        {isEmpty ? (
          <div className="admin-dashboard__panel admin-dashboard__empty">
            <p>Aún no hay concesionarios, listings ni leads registrados.</p>
            <Link to="/admin/dealers/new">Crear primer concesionario</Link>
          </div>
        ) : null}

        <section>
          <h3 className="admin-dashboard__section-title">Oferta (supply)</h3>
          <div className="admin-dashboard__metric-grid">
            <MetricCard label="Total concesionarios" value={summary.supply.totalDealers} />
            <MetricCard label="Activos" value={summary.supply.activeDealers} />
            <MetricCard label="Verificados" value={summary.supply.verifiedDealers} />
            <MetricCard label="En onboarding" value={summary.supply.dealersInOnboarding} />
            <MetricCard label="En trial" value={summary.supply.dealersOnTrial} />
            <MetricCard label="Listings activos" value={summary.supply.activeListings} />
            <MetricCard label="Revisión pendiente" value={summary.supply.listingsNeedingReview} />
            <MetricCard label="Obsoletos" value={summary.supply.listingsStale} />
          </div>
        </section>

        <section>
          <h3 className="admin-dashboard__section-title">Demanda (demand)</h3>
          <div className="admin-dashboard__metric-grid">
            <MetricCard label="Total leads" value={summary.demand.totalLeads} />
            <MetricCard label="Nuevos" value={summary.demand.newLeads} />
            <MetricCard label="Calientes" value={summary.demand.hotLeads} />
            <MetricCard label="En periodo" value={summary.demand.leadsThisMonth} />
            <MetricCard label="Clics WhatsApp" value={summary.demand.whatsappClicks} />
            <MetricCard label="Quiz completados" value={summary.demand.quizCompletions} />
            <MetricCard label="Recomendaciones" value={summary.demand.recommendationRequests} />
          </div>
        </section>

        <section>
          <h3 className="admin-dashboard__section-title">Operaciones</h3>
          <div className="admin-dashboard__metric-grid">
            <MetricCard label="Importaciones OK" value={summary.operations.importsCompleted} />
            <MetricCard label="Importaciones con error" value={summary.operations.importsWithErrors} />
            <MetricCard label="Verificaciones pendientes" value={summary.operations.pendingDealerVerifications} />
            <MetricCard label="Quejas abiertas" value={summary.operations.complaintsOpen} href="/admin/trust-safety/cases" />
            <MetricCard label="Casos críticos" value={summary.operations.trustCriticalCases} href="/admin/trust-safety/cases?severity=critical" />
            <MetricCard label="Reportes nuevos" value={summary.operations.trustNewReports} href="/admin/trust-safety/reports" />
            <MetricCard label="Baja calidad" value={summary.operations.lowQualityListings} />
            <MetricCard label="Duplicados (TODO)" value={summary.operations.duplicateSuspiciousPlaceholder} />
            <MetricCard label="Trials por vencer" value={summary.operations.trialDealersEndingSoon} />
          </div>
        </section>

        <section
          className="admin-dashboard__panel"
          onMouseEnter={() => trackAdminDashboardEvent("admin_launch_readiness_viewed")}
        >
          <h3 className="admin-dashboard__section-title">Preparación para lanzamiento Medellín/Antioquia</h3>
          <div className="admin-dashboard__launch-score">
            <strong style={{ fontSize: "1.5rem" }}>{launchReadiness.score}/100</strong>
            <div className="admin-dashboard__launch-bar">
              <div
                className="admin-dashboard__launch-fill"
                style={{ width: `${launchReadiness.score}%` }}
              />
            </div>
          </div>
          <div className="admin-dashboard__launch-items">
            {launchReadiness.items.map((item) => (
              <div key={item.key} className="admin-dashboard__launch-item">
                <span>
                  {item.label}
                  {item.placeholder ? " (TODO)" : ""}
                  {typeof item.current === "number" && item.target
                    ? ` · ${item.current}/${item.target}`
                    : null}
                </span>
                <span className={`admin-badge ${launchStatusClass(item.status)}`}>
                  {LAUNCH_STATUS_LABELS[item.status]}
                </span>
              </div>
            ))}
          </div>
        </section>

        <section className="admin-dashboard__panel">
          <h3 className="admin-dashboard__section-title">Alertas operativas</h3>
          {!alerts.length ? (
            <p className="muted">Sin alertas prioritarias en este periodo.</p>
          ) : (
            alerts.map((alert) => (
              <div key={alert.id} className={`admin-dashboard-alert ${alertSeverityClass(alert.severity)}`}>
                <div className="admin-dashboard-alert__head">
                  <span>
                    [{ALERT_SEVERITY_LABELS[alert.severity]}] {alert.description}
                  </span>
                  <span>{alert.count}</span>
                </div>
                <p className="admin-dashboard-alert__meta">{alert.recommendedAction}</p>
                {alert.updatedAt ? (
                  <p className="admin-dashboard-alert__meta">Actualizado: {formatDateTime(alert.updatedAt)}</p>
                ) : null}
                <Link
                  to={alert.href}
                  onClick={() => handleAlertClick(alert)}
                  className="admin-link"
                >
                  Ver detalle →
                </Link>
              </div>
            ))
          )}
        </section>

        <section className="admin-dashboard__panel">
          <h3 className="admin-dashboard__section-title">Concesionarios</h3>
          <div className="admin-dashboard__metric-grid" style={{ marginBottom: "1rem" }}>
            <MetricCard label="Prospectos" value={dealerOps.snapshot.newProspects} />
            <MetricCard label="Onboarding" value={dealerOps.snapshot.inOnboarding} />
            <MetricCard label="Verificación pendiente" value={dealerOps.snapshot.pendingVerification} />
            <MetricCard label="Verificados" value={dealerOps.snapshot.verified} />
            <MetricCard label="Trials activos" value={dealerOps.snapshot.activeTrials} />
            <MetricCard label="Trials ≤14 días" value={dealerOps.snapshot.trialsEnding14Days} />
            <MetricCard label="Trials vencidos" value={dealerOps.snapshot.expiredTrials} />
            <MetricCard label="Sin inventario" value={dealerOps.snapshot.zeroActiveListings} />
            <MetricCard label="Baja respuesta" value={dealerOps.snapshot.lowResponseActivity} />
          </div>
          {!dealerOps.rows.length ? (
            <p className="muted">Sin concesionarios registrados.</p>
          ) : (
            <div className="admin-console__table-wrap">
              <table className="admin-dashboard__table-compact">
                <thead>
                  <tr>
                    <th>Concesionario</th>
                    <th>Ciudad</th>
                    <th>Estado</th>
                    <th>Verificación</th>
                    <th>Trial</th>
                    <th>Activos</th>
                    <th>Leads</th>
                    <th>Última actividad</th>
                    <th>Owner</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {dealerOps.rows.map((row) => (
                    <tr key={row.id}>
                      <td>{row.businessName}</td>
                      <td>{row.city || "—"}</td>
                      <td>
                        <span className={`admin-badge ${statusBadgeClass(row.status)}`}>
                          {formatDealerStatus(row.status)}
                        </span>
                      </td>
                      <td>{formatDealerStatus(row.verificationStatus)}</td>
                      <td>{formatDealerStatus(row.trialStatus)}</td>
                      <td>{row.activeListings}</td>
                      <td>{row.leadsReceived}</td>
                      <td>{formatDateTime(row.lastActivity)}</td>
                      <td>{row.assignedSalesOwner || "—"}</td>
                      <td>
                        <Link
                          to={`/admin/dealers/${row.id}`}
                          onClick={() =>
                            trackAdminDashboardEvent("admin_dealer_queue_clicked", { dealerId: row.id })
                          }
                        >
                          Ver
                        </Link>
                        {row.trialStatus === "active" || row.trialStatus === "ending_soon" ? (
                          <>
                            {" · "}
                            <Link to="/admin/dealers/trials">Trial</Link>
                          </>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section className="admin-dashboard__panel">
          <div className="admin-console__header-actions" style={{ justifyContent: "space-between", marginBottom: "0.5rem" }}>
            <h3 className="admin-dashboard__section-title" style={{ margin: 0 }}>Inventario</h3>
            <Link className="admin-btn admin-btn--ghost" to="/admin/inventory-quality">Calidad de inventario →</Link>
          </div>
          <div className="admin-dashboard__two-col">
            <div>
              <div className="admin-dashboard__metric-grid">
                <MetricCard label="Total" value={inventoryOps.counts.total} />
                <MetricCard label="Activos" value={inventoryOps.counts.active} />
                <MetricCard label="Borrador" value={inventoryOps.counts.draft} />
                <MetricCard label="Revisión" value={inventoryOps.counts.needsReview} />
                <MetricCard label="Pausados" value={inventoryOps.counts.paused} />
                <MetricCard label="Vendidos" value={inventoryOps.counts.sold} />
                <MetricCard label="Archivados" value={inventoryOps.counts.archived} />
                <MetricCard label="Obsoletos" value={inventoryOps.counts.stale} />
                <MetricCard label="Calidad baja" value={inventoryOps.counts.lowQuality} />
                <MetricCard label="Duplicados" value={inventoryOps.counts.duplicateCandidates} />
                <MetricCard label="Atípicos" value={inventoryOps.counts.outlierFlags} />
                <MetricCard label="Seguimientos" value={inventoryOps.counts.openFollowUps} />
              </div>
            </div>
            <div>
              <MetricCard label="Cobertura fotos" value={formatPercent(inventoryOps.health.photoCoveragePct)} />
              <MetricCard label="Frescura" value={formatPercent(inventoryOps.health.freshnessPct)} />
              <MetricCard label="Calidad promedio" value={inventoryOps.health.averageQualityScore ?? "—"} />
              <MetricCard label="% obsoleto" value={formatPercent(inventoryOps.health.staleListingRatePct)} />
              <MetricCard label="Activos/concesionario" value={inventoryOps.health.activeListingsPerDealer} />
            </div>
          </div>
          <h4 className="admin-dashboard__section-title">Colas de issues</h4>
          <ul className="admin-dashboard__queue-list">
            {inventoryOps.queues.map((queue) => (
              <li key={queue.key}>
                <span>{queue.label}</span>
                <Link
                  to={queue.href}
                  onClick={() =>
                    trackAdminDashboardEvent("admin_inventory_queue_clicked", { queue: queue.key })
                  }
                >
                  {queue.count} →
                </Link>
              </li>
            ))}
          </ul>
        </section>

        <section className="admin-dashboard__panel">
          <h3 className="admin-dashboard__section-title">Leads</h3>
          <div className="admin-dashboard__metric-grid">
            <MetricCard label="Nuevos" value={leadOps.statusCounts.new} />
            <MetricCard label="WhatsApp" value={leadOps.statusCounts.whatsappOpened} />
            <MetricCard label="Contactados" value={leadOps.statusCounts.contacted} />
            <MetricCard label="Sin respuesta" value={leadOps.statusCounts.noResponse} />
            <MetricCard label="Test drive" value={leadOps.statusCounts.testDriveScheduled} />
            <MetricCard label="Negociando" value={leadOps.statusCounts.negotiating} />
            <MetricCard label="Vendidos" value={leadOps.statusCounts.sold} />
            <MetricCard label="Perdidos" value={leadOps.statusCounts.lost} />
            <MetricCard label="Malos" value={leadOps.statusCounts.badLead} />
            <MetricCard label="Sin acción dealer" value={leadOps.statusCounts.withoutDealerAction} />
            <MetricCard label="Calientes sin contacto" value={leadOps.statusCounts.hotNotContacted} />
          </div>
          <div className="admin-dashboard__two-col" style={{ marginTop: "1rem" }}>
            <div>
              <h4 className="admin-dashboard__section-title">Calidad</h4>
              {Object.entries(leadOps.quality.counts).map(([key, count]) => (
                <BarRow key={key} label={key} value={count} max={leadOps.statusCounts.new || 1} />
              ))}
              <p className="muted">
                Score promedio: {leadOps.quality.averageScore ?? "—"} · Hot ratio:{" "}
                {leadOps.quality.hotLeadRatioPct}%
              </p>
            </div>
            <div>
              <h4 className="admin-dashboard__section-title">Cola reciente</h4>
              {!leadOps.queue.length ? (
                <p className="muted">Sin leads.</p>
              ) : (
                <table className="admin-dashboard__table-compact">
                  <thead>
                    <tr>
                      <th>Lead</th>
                      <th>Concesionario</th>
                      <th>Score</th>
                      <th>Estado</th>
                      <th>Edad (h)</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {leadOps.queue.map((lead) => (
                      <tr key={lead.id}>
                        <td>{lead.buyerName || lead.vehicleOrPreference}</td>
                        <td>{lead.dealerName}</td>
                        <td>{lead.score ?? "—"}</td>
                        <td>{STATUS_LABELS[lead.status] || lead.status}</td>
                        <td>{lead.ageHours}</td>
                        <td>
                          <Link
                            to={`/admin/leads/${lead.id}`}
                            onClick={() =>
                              trackAdminDashboardEvent("admin_lead_queue_clicked", { leadId: lead.id })
                            }
                          >
                            Ver
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </section>

        <section className="admin-dashboard__panel">
          <h3 className="admin-dashboard__section-title">Importaciones</h3>
          <div className="admin-dashboard__metric-grid">
            <MetricCard label="Recientes" value={importHealth.summary.recent} />
            <MetricCard label="Completadas" value={importHealth.summary.completed} />
            <MetricCard label="Con errores" value={importHealth.summary.withErrors} />
            <MetricCard label="Fallidas" value={importHealth.summary.failed} />
            <MetricCard label="Filas importadas" value={importHealth.summary.rowsImported} />
            <MetricCard label="Filas omitidas" value={importHealth.summary.rowsSkipped} />
            <MetricCard label="Filas error" value={importHealth.summary.errorRows} />
            <MetricCard label="Duplicados" value={importHealth.summary.duplicateRows} />
          </div>
          {!importHealth.rows.length ? (
            <p className="muted" style={{ marginTop: "0.75rem" }}>
              Sin importaciones en el periodo.{" "}
              <Link to="/admin/inventory/imports/new">Iniciar importación</Link>
            </p>
          ) : (
            <table className="admin-dashboard__table-compact" style={{ marginTop: "0.75rem" }}>
              <thead>
                <tr>
                  <th>Concesionario</th>
                  <th>Estado</th>
                  <th>Importadas</th>
                  <th>Errores</th>
                  <th>Fecha</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {importHealth.rows.map((row) => (
                  <tr key={row.id}>
                    <td>{row.dealerName}</td>
                    <td>{row.status}</td>
                    <td>{row.importedRowCount}</td>
                    <td>{row.errorRowCount}</td>
                    <td>{formatDateTime(row.createdAt)}</td>
                    <td>
                      <Link
                        to={`/admin/inventory/imports/${row.id}`}
                        onClick={() =>
                          trackAdminDashboardEvent("admin_import_queue_clicked", { importId: row.id })
                        }
                      >
                        Ver
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        {planMonetizationSummary ? (
          <section className="admin-dashboard__panel">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "0.5rem" }}>
              <h3 className="admin-dashboard__section-title">Planes y monetización</h3>
              <Link className="admin-link" to="/admin/plans">Abrir planes →</Link>
            </div>
            <div className="admin-dashboard__metric-grid">
              <MetricCard label="Gratis" value={planMonetizationSummary.freeDealers} href="/admin/plans/dealers?planKey=free" />
              <MetricCard label="En trial" value={planMonetizationSummary.trialDealers} href="/admin/dealers/trials" />
              <MetricCard label="Pagados" value={planMonetizationSummary.paidDealers} href="/admin/plans/dealers" />
              <MetricCard label="Listos convertir" value={planMonetizationSummary.readyToConvert} href="/admin/dealers/trials/ready-to-convert" />
              <MetricCard label="Sobre límite listings" value={planMonetizationSummary.overListingLimit} href="/admin/plans/dealers?overListingLimit=true" />
              <MetricCard label="Cerca límite leads" value={planMonetizationSummary.nearLeadLimit} href="/admin/plans/dealers?nearLeadLimit=true" />
              <MetricCard label="Factura manual pend." value={planMonetizationSummary.pendingManualInvoice} />
              <MetricCard label="Conversiones mes" value={planMonetizationSummary.conversionsThisMonth} />
            </div>
          </section>
        ) : null}

        {cityExpansionSummary ? (
          <section className="admin-dashboard__panel">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "0.5rem" }}>
              <h3 className="admin-dashboard__section-title">Expansión por ciudad</h3>
              <Link className="admin-link" to="/admin/expansion">Abrir expansión →</Link>
            </div>
            <div className="admin-dashboard__metric-grid">
              <MetricCard label="En pipeline" value={cityExpansionSummary.citiesInPipeline} href="/admin/expansion/cities" />
              <MetricCard label="Soft launch ready" value={cityExpansionSummary.softLaunchReady} href="/admin/expansion" />
              <MetricCard label="Necesitan supply" value={cityExpansionSummary.needingSupply} />
              <MetricCard label="Baja frescura" value={cityExpansionSummary.lowFreshness} />
              <MetricCard
                label="Próxima ciudad"
                value={cityExpansionSummary.nextTargetCity?.city || "—"}
                href={cityExpansionSummary.nextTargetCity ? `/admin/expansion/cities/${cityExpansionSummary.nextTargetCity.citySlug}` : undefined}
              />
            </div>
          </section>
        ) : null}

        {partnershipSummary ? (
          <section className="admin-dashboard__panel">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "0.5rem" }}>
              <h3 className="admin-dashboard__section-title">Partnerships</h3>
              <Link className="admin-link" to="/admin/partners">Abrir aliados →</Link>
            </div>
            <div className="admin-dashboard__metric-grid">
              <MetricCard label="Leads esta semana" value={partnershipSummary.partnerLeadsThisWeek} href="/admin/leads?tab=partner" />
              <MetricCard label="Aliados activos" value={partnershipSummary.activePartners} href="/admin/partners" />
              <MetricCard label="Sin asignar" value={partnershipSummary.unassignedPartnerLeads} href="/admin/leads?tab=partner" />
              <MetricCard label="Top categoría" value={partnershipSummary.topCategory} />
            </div>
          </section>
        ) : null}

        <section className="admin-dashboard__panel">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "0.5rem" }}>
            <h3 className="admin-dashboard__section-title">Seguimiento de trials</h3>
            <Link className="admin-link" to="/admin/dealers/trials">
              Abrir dashboard trials →
            </Link>
          </div>
          <div className="admin-dashboard__metric-grid">
            <MetricCard label="Activos" value={trialPerformance.snapshot.activeTrials} href="/admin/dealers/trials/active" />
            <MetricCard label="Por vencer" value={trialPerformance.snapshot.endingSoon} href="/admin/dealers/trials/ending-soon" />
            <MetricCard label="Listos convertir" value={trialPerformance.snapshot.readyToConvert} href="/admin/dealers/trials/ready-to-convert" />
            <MetricCard label="Atención" value={trialPerformance.snapshot.needsAttention} href="/admin/dealers/trials/needs-attention" />
            <MetricCard label="Vencidos sin decisión" value={trialPerformance.snapshot.expiredWithoutDecision} href="/admin/dealers/trials" />
            <MetricCard label="Convertidos mes" value={trialPerformance.snapshot.convertedThisMonth} />
            <MetricCard label="Perdidos mes" value={trialPerformance.snapshot.lostThisMonth} />
            <MetricCard label="Readiness prom." value={trialPerformance.snapshot.averageReadinessScore} />
          </div>
          {!trialPerformance.rows.length ? (
            <p className="muted" style={{ marginTop: "0.75rem" }}>Sin trials registrados.</p>
          ) : (
            <table className="admin-dashboard__table-compact" style={{ marginTop: "0.75rem" }}>
              <thead>
                <tr>
                  <th>Concesionario</th>
                  <th>Días restantes</th>
                  <th>Leads</th>
                  <th>Activos</th>
                  <th>Vendidos</th>
                  <th>Respuesta %</th>
                  <th>Acción</th>
                </tr>
              </thead>
              <tbody>
                {trialPerformance.rows.map((row) => (
                  <tr key={row.id}>
                    <td>{row.businessName}</td>
                    <td>{row.daysRemaining ?? "—"}</td>
                    <td>{row.leadsReceived}</td>
                    <td>{row.activeListings}</td>
                    <td>{row.soldOutcomes}</td>
                    <td>{row.responseActivity}%</td>
                    <td>
                      <Link
                        to={`/admin/dealers/${row.id}/trial`}
                        onClick={() =>
                          trackAdminDashboardEvent("admin_trial_queue_clicked", { dealerId: row.id })
                        }
                      >
                        {row.recommendedAction}
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        <section className="admin-dashboard__panel">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "0.5rem" }}>
            <h3 className="admin-dashboard__section-title">Lanzamiento · dealers fundadores</h3>
            <Link className="admin-link" to="/admin/dealers/founding">
              Ver dashboard fundadores →
            </Link>
          </div>
          {foundingDealerSummary?.summary ? (
            <div className="admin-dashboard__metric-grid">
              <MetricCard label="Launch-ready" value={foundingDealerSummary.summary.launchReady} href="/admin/dealers/founding/readiness" />
              <MetricCard label="En onboarding" value={foundingDealerSummary.summary.onboarding} href="/admin/dealers/founding/pipeline" />
              <MetricCard label="Activos" value={foundingDealerSummary.summary.active} />
              <MetricCard label="Tareas abiertas" value={foundingDealerSummary.summary.openTasks} href="/admin/dealers/founding/tasks" />
              <MetricCard
                label="Listings limpios"
                value={foundingDealerSummary.cohort?.metrics?.cleanListings ?? "—"}
                href="/admin/dealers/founding/readiness"
              />
              <MetricCard
                label="Frescura inventario"
                value={formatPercent(foundingDealerSummary.cohort?.metrics?.freshnessPct)}
                href="/admin/dealers/founding/imports"
              />
            </div>
          ) : (
            <p className="muted">Sin datos de cohorte fundador aún.</p>
          )}
        </section>

        <section className="admin-dashboard__panel">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "0.5rem" }}>
            <h3 className="admin-dashboard__section-title">Lanzamiento · campaña compradores</h3>
            <Link className="admin-link" to="/admin/campaigns/launch">
              Ver launch dashboard →
            </Link>
          </div>
          {buyerLaunchOverlay ? (
            <>
              <p className="muted" style={{ marginBottom: "0.75rem" }}>
                Estado:{" "}
                <span className={launchStatusClass(buyerLaunchOverlay.launchStatus)}>
                  {LAUNCH_STATUS_LABELS[buyerLaunchOverlay.launchStatus] || buyerLaunchOverlay.launchStatus}
                </span>
                {buyerLaunchOverlay.startDate ? (
                  <> · {buyerLaunchOverlay.startDate.slice(0, 10)}
                  {buyerLaunchOverlay.endDate ? ` → ${buyerLaunchOverlay.endDate.slice(0, 10)}` : ""}</>
                ) : null}
              </p>
              <div className="admin-dashboard__metric-grid">
                <MetricCard
                  label="Leads en launch"
                  value={buyerLaunchOverlay.leadsDuringLaunch ?? "—"}
                  href="/admin/campaigns/launch/leads"
                />
                <MetricCard
                  label="Contrib. campañas"
                  value={buyerLaunchOverlay.campaignContribution ?? "—"}
                  href="/admin/analytics/campaigns"
                />
                <MetricCard
                  label="Contrib. SEO"
                  value={buyerLaunchOverlay.seoContribution ?? "—"}
                  href="/admin/analytics/seo"
                />
                <MetricCard
                  label="Readiness"
                  value="Revisar"
                  href="/admin/campaigns/launch/readiness"
                />
                <MetricCard
                  label="Daily monitor"
                  value="Hoy"
                  href="/admin/campaigns/launch/daily-monitor"
                />
              </div>
            </>
          ) : (
            <p className="muted">Configura la campaña de lanzamiento para ver métricas aquí.</p>
          )}
        </section>

        <section className="admin-dashboard__panel" id="analytics">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "0.5rem" }}>
            <h3 className="admin-dashboard__section-title">Salud del marketplace</h3>
            <Link className="admin-link" to="/admin/analytics/marketplace">
              Ver analytics completo →
            </Link>
          </div>
          {marketplaceAnalyticsSummary?.snapshot ? (
            <>
              <div className="admin-dashboard__metric-grid" style={{ marginBottom: "1rem" }}>
                <MetricCard
                  label="Listings activos"
                  value={marketplaceAnalyticsSummary.snapshot.totalActiveListings}
                  href="/admin/analytics/supply"
                />
                <MetricCard
                  label="Dealers activos"
                  value={marketplaceAnalyticsSummary.snapshot.activeDealers}
                  href="/admin/analytics/dealers"
                />
                <MetricCard
                  label="Sesiones compradores"
                  value={marketplaceAnalyticsSummary.snapshot.totalBuyerSessions}
                  href="/admin/analytics/demand"
                />
                <MetricCard label="Leads" value={marketplaceAnalyticsSummary.snapshot.totalLeads} href="/admin/analytics/demand" />
                <MetricCard
                  label="Frescura inventario"
                  value={formatPercent(marketplaceAnalyticsSummary.snapshot.inventoryFreshnessPercent)}
                  href="/admin/analytics/inventory-health"
                />
                <MetricCard
                  label="Casos confianza"
                  value={marketplaceAnalyticsSummary.snapshot.trustSafetyOpenCases}
                  href="/admin/analytics/trust-safety"
                />
                <MetricCard
                  label="Top ciudad"
                  value={marketplaceAnalyticsSummary.topCities?.[0]?.city || marketplaceHealth.supplyByCity?.[0]?.key || "—"}
                  href="/admin/analytics/cities"
                />
                <MetricCard
                  label="Top fuente"
                  value={marketplaceAnalyticsSummary.topSources?.[0]?.key || "—"}
                  href="/admin/analytics/campaigns"
                />
              </div>
              {marketplaceAnalyticsSummary.recommendedActions?.length ? (
                <div style={{ marginBottom: "1rem" }}>
                  <h4 className="admin-dashboard__section-title">Acciones recomendadas</h4>
                  <ul className="admin-dashboard__activity-feed">
                    {marketplaceAnalyticsSummary.recommendedActions.slice(0, 5).map((action) => (
                      <li key={action.id}>
                        <Link to={action.link}>{action.message}</Link>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </>
          ) : null}
          <div className="admin-dashboard__two-col">
            <div>
              <h4 className="admin-dashboard__section-title">Oferta por ciudad</h4>
              {marketplaceHealth.supplyByCity.map((item) => (
                <BarRow
                  key={item.key}
                  label={item.key}
                  value={item.count}
                  max={marketplaceHealth.supplyByCity[0]?.count || 1}
                />
              ))}
              <h4 className="admin-dashboard__section-title">Leads por ciudad</h4>
              {marketplaceHealth.leadsByCity.map((item) => (
                <BarRow
                  key={item.key}
                  label={item.key}
                  value={item.count}
                  max={marketplaceHealth.leadsByCity[0]?.count || 1}
                />
              ))}
            </div>
            <div>
              <p>
                Precio promedio: {formatCop(marketplaceHealth.averagePrice)} · Mediana:{" "}
                {formatCop(marketplaceHealth.medianPrice)}
              </p>
              <p>Quiz completados: {marketplaceHealth.quizCompletions}</p>
              <p className="muted">Búsquedas y términos top: TODO</p>
              <h4 className="admin-dashboard__section-title">Top listings por leads</h4>
              {marketplaceHealth.topLeadGeneratingListings.map((item) => (
                <BarRow key={item.id} label={item.label} value={item.count} max={marketplaceHealth.topLeadGeneratingListings[0]?.count || 1} />
              ))}
              <h4 className="admin-dashboard__section-title">Top concesionarios por leads</h4>
              {marketplaceHealth.topLeadGeneratingDealers.map((item) => (
                <BarRow key={item.id} label={item.label} value={item.count} max={marketplaceHealth.topLeadGeneratingDealers[0]?.count || 1} />
              ))}
            </div>
          </div>
        </section>

        <section className="admin-dashboard__panel">
          <h3 className="admin-dashboard__section-title">Actividad reciente</h3>
          {!recentActivity.length ? (
            <p className="muted">Sin actividad en el periodo seleccionado.</p>
          ) : (
            <ul className="admin-dashboard__activity-feed">
              {recentActivity.map((item) => (
                <li key={item.id}>
                  <strong>{item.title}</strong> — {item.description}
                  <time>{formatDateTime(item.createdAt)}</time>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </AdminPageLayout>
  );
}

export default function AdminDashboardPage() {
  return (
    <RequirePermission permission={ADMIN_PERMISSIONS.REAL_ESTATE_SOURCES_VIEW}>
      <div className="midpoint-app account-area-app">
        <AdminDashboardPageContent />
      </div>
    </RequirePermission>
  );
}
